import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.js';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { DISABLED_ANSWERS } from './constants';
import { TExplanationAnswerComment, TPurposeOfBusinessRelations } from './types';
import { checkComment, checkServices, getCommentContextInfo } from './utils';
import { licensesSchema, phoneSchema } from '../../validationSchemas';
import { checkTableField } from '../../utils';
import { blockedDomains } from '~/constants/common.ts';

const stringNumberSchema = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('is-non-negative', errorMessages.MUST_BE_POSITIVE_OR_ZERO, (value) => {
    if (!value) return false;
    if (value.trim().startsWith('-')) return false;
    const num = Number(value);
    if (Number.isNaN(num)) return true;
    return num >= 0;
  })
  .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
  .test('only-digits-and-dot', errorMessages.MUST_BE_NUMBER, (value) => {
    if (!value) return false;
    if (!regularExpressions.JUST_DOT.test(value)) return false;
    if ((value.match(/\./g) || []).length > 1) return false;
    return true;
  })
  .test('max-2-decimals', errorMessages.MAX_LENGTH_OF_CHARACTERS(2), (value) => {
    if (!value) return false;
    const parts = value.split('.');
    const dec = parts[1];
    if (dec && dec.length > 2) return false;
    return true;
  });

const commentSchema = Yup.object()
  .test('comment-check-fullName', function (commentObj) {
    return checkComment.call(this, commentObj, 'fullName');
  })
  .test('comment-check-RNOKPP', errorMessages.FIELD_REQUIRED, function (commentObj) {
    return checkComment.call(this, commentObj, 'RNOKPP');
  })
  .test('comment-check', errorMessages.FIELD_REQUIRED, function (commentObj) {
    const { answerValue, questionId } = getCommentContextInfo(this);
    if (questionId === 9 && answerValue !== 26) return true;
    if (DISABLED_ANSWERS.includes(answerValue)) {
      return true;
    }
    if (!(commentObj as TExplanationAnswerComment)?.general?.trim()) {
      return this.createError({
        path: `${this.path}.general`,
        message: errorMessages.FIELD_REQUIRED,
      });
    }
    return true;
  });

const conflictZoneYesNoSchema = Yup.boolean()
  .nullable()
  .when('isPremisesInConflictZone', {
    is: true,
    then: (schema) => schema.required(errorMessages.YES_NO_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  });

const securitiesOperationsSchema = Yup.boolean()
  .nullable()
  .when('securitiesOperations', {
    is: true,
    then: (schema) => schema.required(errorMessages.YES_NO_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  });

const securitiesOpsCrossSchema = (params: string) =>
  Yup.boolean()
    .nullable()
    .when('securitiesOperations', {
      is: true,
      then: (schema) =>
        schema
          .required(errorMessages.YES_NO_REQUIRED)
          .test('not-both-false', errorMessages.ONLY_ONE_PERSON, function (value) {
            const siblingValue = this.parent[params];
            return !(value === false && siblingValue === false);
          }),
      otherwise: (schema) => schema.notRequired(),
    });

const infoContactsTableSchema = (tableCheck: string) =>
  Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .nullable()
        .test('language-consistency', errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY, (value) => {
          if (!value) return true;
          const isUkr = regularExpressions.CYRILLIC.test(value);
          const isLatin = regularExpressions.LATIN.test(value);
          return !(isUkr && isLatin);
        }),
      phone: Yup.string()
        .nullable()
        .test('phone-validation', errorMessages.INVALID_INPUT_DATA, function (value) {
          if (!value) return true;
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length < 5) return true;
          return phoneSchema.isValidSync(value);
        }),
      email: Yup.string()
        .nullable()
        .test('required', errorMessages.FIELD_REQUIRED, function (value) {
          return checkTableField.call(this, tableCheck, value);
        })
        .test('email-format', errorMessages.INVALID_EMAIL_FORMAT, function (value) {
          if (!value?.trim()) return true;
          const tableEnabled = this.from?.[1]?.value[tableCheck] === true;
          if (!tableEnabled) return true;
          return Yup.string().email().isValidSync(value);
        })
        .test('blocked-domain', errorMessages.BLOCKED_EMAIL_DOMAIN, function (value) {
          if (!value?.trim()) return true;
          const tableEnabled = this.from?.[1]?.value[tableCheck] === true;
          if (!tableEnabled) return true;
          const domain = value.split('@')[1];
          const isBlocked = blockedDomains.some(
            (blocked) => domain === blocked || domain?.endsWith(`.${blocked}`)
          );
          return isBlocked
            ? this.createError({ path: this.path, message: errorMessages.BLOCKED_EMAIL_DOMAIN })
            : true;
        })
        .test('unique-email', errorMessages.UNIQUE_EMAIL, function (value) {
          if (!value?.trim()) return true;

          const tableEnabled = this.from?.[1]?.value[tableCheck] === true;
          if (!tableEnabled) return true;

          const arrayKey = this.path.split('[')[0] ?? -1;
          const siblings = this.from?.[1]?.value[arrayKey] as Array<{
            email?: string | null;
          }>;

          if (!Array.isArray(siblings)) return true;

          const currentIndex = Number(this.path.match(/\[(\d+)\]/)?.[1]);

          const isDuplicate = siblings.some(
            (item, index) =>
              index !== currentIndex &&
              item.email?.trim().toLowerCase() === value.trim().toLowerCase()
          );

          return isDuplicate
            ? this.createError({ path: this.path, message: errorMessages.UNIQUE_EMAIL })
            : true;
        }),
    })
  );

const typesOfActivitySchema = Yup.object({
  label: Yup.string(),
  value: Yup.string().nullable().notRequired(),
})
  .test('types-filled', errorMessages.FIELD_REQUIRED, function (val) {
    if (!val?.label?.trim()) {
      return this.createError({
        path: 'typesOfActivity',
        message: errorMessages.FIELD_REQUIRED,
      });
    }
    return true;
  })
  .required(errorMessages.FIELD_REQUIRED);

const explanationSchema = Yup.array()
  .of(
    Yup.object({
      questionId: Yup.number().required(),
      answers: Yup.array()
        .of(
          Yup.object({
            value: Yup.string().required(),
            label: Yup.string().required(),
            comments: Yup.array().of(commentSchema),
          })
        )
        .required()
        .test('answers-required', function (value) {
          if (!Array.isArray(value) || !value.length || !value[0]?.value) {
            return this.createError({
              path: this.path,
              message: errorMessages.FIELD_REQUIRED,
            });
          }
          return true;
        }),
    })
  )
  .min(1, errorMessages.FILL_CLARIFYING_INFORMATION);

export const soleTraderValidationSchema = Yup.object()
  .shape({
    businessDescription: Yup.string().required(errorMessages.FIELD_REQUIRED),
    isPremisesInConflictZone: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
    typesOfActivity: typesOfActivitySchema,
    cashManagement: Yup.boolean().nullable(),
    loans: Yup.boolean().nullable(),
    deposits: Yup.boolean().nullable(),
    tradeFinanceAndDocumentaryOperations: Yup.boolean().nullable(),
    securitiesOperations: Yup.boolean().nullable(),
    paymentCards: Yup.boolean().nullable(),
    other: Yup.string().nullable(),
    type: Yup.string().notRequired().nullable(),
    explanation: explanationSchema,
  })
  .test('exactly-one-service', function (value) {
    return checkServices.call(this, value as TPurposeOfBusinessRelations);
  });

export const reIdentificationSoleTraderValidationSchema = Yup.object()
  .shape({
    businessDescription: Yup.string().required(errorMessages.FIELD_REQUIRED),
    isPremisesInConflictZone: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
    typesOfActivity: typesOfActivitySchema,
    cashManagement: Yup.boolean().nullable(),
    loans: Yup.boolean().nullable(),
    deposits: Yup.boolean().nullable(),
    tradeFinanceAndDocumentaryOperations: Yup.boolean().nullable(),
    securitiesOperations: Yup.boolean().nullable(),
    paymentCards: Yup.boolean().nullable(),
    other: Yup.string().nullable(),
    hasBusinessActivityChanges: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
    explanation: Yup.mixed().when('hasBusinessActivityChanges', {
      is: true,
      then: () => explanationSchema,
      otherwise: () => Yup.mixed().nullable().notRequired(),
    }),
  })
  .test('exactly-one-service', function (value) {
    return checkServices.call(this, value as TPurposeOfBusinessRelations);
  });

export const legalResidentValidationSchema = Yup.object()
  .shape({
    businessDescription: Yup.string()
      .required(errorMessages.FIELD_REQUIRED)
      .max(4000, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(4000)),
    registeredShareCapital: stringNumberSchema,
    paidShareCapital: stringNumberSchema,
    isLicensed: Yup.boolean().required(errorMessages.YES_NO_REQUIRED),
    typesOfActivity: typesOfActivitySchema,
    licenses: licensesSchema('isLicensed'),
    isNonProfitOrganization: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
    nonProfitDetails: Yup.array().of(
      Yup.object().shape({
        code: Yup.string()
          .nullable()
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, 'isNonProfitOrganization', value);
          }),
        decisionDate: Yup.string()
          .nullable()
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, 'isNonProfitOrganization', value);
          }),
        decisionNumber: Yup.string()
          .nullable()
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, 'isNonProfitOrganization', value);
          }),
        registrationAuthority: Yup.string()
          .nullable()
          .test('required', errorMessages.FIELD_REQUIRED, function (value) {
            return checkTableField.call(this, 'isNonProfitOrganization', value);
          }),
      })
    ),
    isNewOrNonProfitEntity: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
    explanation: Yup.mixed().when('isNewOrNonProfitEntity', {
      is: true,
      then: () => explanationSchema,
      otherwise: () => Yup.mixed().nullable().notRequired(),
    }),
    isPremisesInConflictZone: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
    premisesTypesInConflictZone: Yup.array()
      .transform((value, originalValue) => (originalValue == null ? [] : value))
      .when('isPremisesInConflictZone', {
        is: true,
        then: (schema) => schema.min(1, 'Будь ласка, оберіть хоча б один варіант зі списку'),
        otherwise: (schema) => schema.notRequired(),
      }),
    arePremisesUsedInWarZones: conflictZoneYesNoSchema,
    isIncomeReceivedFromWarZones: conflictZoneYesNoSchema,
    isEmployeesReceiveSalary: conflictZoneYesNoSchema,
    isReRegistrationAgreed: conflictZoneYesNoSchema,
    cashManagement: Yup.boolean().nullable(),
    loans: Yup.boolean().nullable(),
    deposits: Yup.boolean().nullable(),
    tradeFinanceAndDocumentaryOperations: Yup.boolean().nullable(),
    securitiesOperations: Yup.boolean().nullable(),
    other: Yup.string().nullable(),
    isTradingInfoOnCapitalMarketsProvided: securitiesOperationsSchema,
    tradingInfoContacts: infoContactsTableSchema('isTradingInfoOnCapitalMarketsProvided'),
    isDepositoryInfoOnCapitalMarketsProvided: securitiesOpsCrossSchema(
      'isTradingInfoOnCapitalMarketsProvided'
    ),
    depositoryInfoContacts: infoContactsTableSchema('isDepositoryInfoOnCapitalMarketsProvided'),
    type: Yup.string().notRequired().nullable(),
  })
  .test('exactly-one-service', function (value) {
    return checkServices.call(this, value as TPurposeOfBusinessRelations);
  });
